/**
 * API client for Industry Research v2.0 endpoints
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '../api';
import type {
  IndustrySummary,
  IndustryDetail,
  CheckOrCreateIndustryRequest,
  CheckOrCreateIndustryResponse,
  GenerateIndustryResearchRequest,
  IndustryStatus,
  DataRefreshStatus,
  MarketData,
  CompetitiveLandscape,
  TechnologyTrends,
  Disruptions,
  ExecutiveSummary,
  MarketLeader,
  IndustryBenchmarkResponse,
} from '../../types/industryResearch';

const BASE_URL = '/industries';

// ============================================================================
// INDUSTRY CRUD OPERATIONS
// ============================================================================

/**
 * List all industries with optional filtering
 * Note: For search, use searchIndustries() function instead
 */
export const listIndustries = async (
  status?: IndustryStatus,
  category?: string,
  skip: number = 0,
  limit: number = 50
): Promise<IndustrySummary[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (category) params.append('category', category);
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());

  const response = await apiClient.get<IndustrySummary[]>(
    `${BASE_URL}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get detailed industry information with research data
 */
export const getIndustry = async (
  industryId: string,
  includeResearch: boolean = true
): Promise<IndustryDetail> => {
  const params = new URLSearchParams();
  params.append('include_research', includeResearch.toString());

  // Backend returns nested structure: { industry: {...}, marketData: ..., etc }
  interface BackendDetailedResponse {
    industry: IndustrySummary;
    currentVersion: any;
    marketData?: MarketData;
    competitiveLandscape?: CompetitiveLandscape;
    technologyTrends?: TechnologyTrends;
    disruptions?: Disruptions;
    executiveSummary?: ExecutiveSummary;
  }

  const response = await apiClient.get<BackendDetailedResponse>(
    `${BASE_URL}/${industryId}?${params.toString()}`
  );

  // Flatten the response to match IndustryDetail interface
  return {
    ...response.data.industry,  // Spread industry base fields
    marketData: response.data.marketData,
    competitiveLandscape: response.data.competitiveLandscape,
    technologyTrends: response.data.technologyTrends,
    disruptions: response.data.disruptions,
    executiveSummary: response.data.executiveSummary,
  };
};

/**
 * Search industries by name or description
 */
export const searchIndustries = async (
  query: string,
  limit: number = 20
): Promise<IndustrySummary[]> => {
  const params = new URLSearchParams();
  params.append('q', query);
  params.append('limit', limit.toString());

  const response = await apiClient.get<IndustrySummary[]>(
    `${BASE_URL}/search?${params.toString()}`
  );
  return response.data;
};

/**
 * Delete industry (soft delete - marks as archived)
 */
export const deleteIndustry = async (
  industryId: string,
  deletedBy: string = 'system'
): Promise<void> => {
  const params = new URLSearchParams();
  params.append('deleted_by', deletedBy);

  await apiClient.delete(`${BASE_URL}/${industryId}?${params.toString()}`);
};

// ============================================================================
// CHECK-OR-CREATE OPERATION (UI Integration)
// ============================================================================

/**
 * Check if industry exists, or create stub if not
 * Used for company search integration and manual entry
 */
export const checkOrCreateIndustry = async (
  request: CheckOrCreateIndustryRequest,
  createdBy: string = 'system'
): Promise<CheckOrCreateIndustryResponse> => {
  const params = new URLSearchParams();
  params.append('created_by', createdBy);

  const response = await apiClient.post<CheckOrCreateIndustryResponse>(
    `${BASE_URL}/check-or-create?${params.toString()}`,
    request
  );
  return response.data;
};

// ============================================================================
// RESEARCH GENERATION
// ============================================================================

/**
 * Generate or refresh industry research using LLM
 * For new industries: creates v1.0.0
 * For existing industries: may create new version if material changes detected
 */
export const generateIndustryResearch = async (
  industryId: string,
  forceRefresh: boolean = false,
  createdBy: string = 'system'
): Promise<IndustryDetail> => {
  const params = new URLSearchParams();
  params.append('force_refresh', forceRefresh.toString());
  params.append('created_by', createdBy);

  // Backend returns nested structure (same as getIndustry)
  interface BackendDetailedResponse {
    industry: IndustrySummary;
    currentVersion: any;
    marketData?: MarketData;
    competitiveLandscape?: CompetitiveLandscape;
    technologyTrends?: TechnologyTrends;
    disruptions?: Disruptions;
    executiveSummary?: ExecutiveSummary;
  }

  const response = await apiClient.post<BackendDetailedResponse>(
    `${BASE_URL}/${industryId}/research?${params.toString()}`
  );

  // Flatten the response to match IndustryDetail interface (same as getIndustry)
  return {
    ...response.data.industry,
    marketData: response.data.marketData,
    competitiveLandscape: response.data.competitiveLandscape,
    technologyTrends: response.data.technologyTrends,
    disruptions: response.data.disruptions,
    executiveSummary: response.data.executiveSummary,
  };
};

/**
 * Create new industry with immediate research generation
 * (Alternative to check-or-create + generate-research)
 */
export const createIndustryWithResearch = async (
  request: GenerateIndustryResearchRequest,
  createdBy: string = 'system'
): Promise<IndustryDetail> => {
  const params = new URLSearchParams();
  params.append('created_by', createdBy);

  const response = await apiClient.post<IndustryDetail>(
    `${BASE_URL}?${params.toString()}`,
    request
  );
  return response.data;
};

// ============================================================================
// VERSION MANAGEMENT
// ============================================================================

export interface IndustryVersion {
  versionId: string;
  entityId: string;
  version: string; // temporal version "2025-Q4.1"
  semanticVersion: string; // "v1.2.0"
  timestamp: string;
  validFrom: string;
  validTo?: string;
  reportPeriod: string;
  changeType: 'major' | 'minor' | 'patch';
  materialityScore: number;
  materialityAssessment: 'major' | 'minor' | 'patch' | 'immaterial';
  keyChanges: string[];
  createdAt: string;
  createdBy: string;
}

/**
 * List all versions for an industry
 */
export const listIndustryVersions = async (
  industryId: string,
  includeSuperseded: boolean = false
): Promise<IndustryVersion[]> => {
  const params = new URLSearchParams();
  params.append('include_superseded', includeSuperseded.toString());

  const response = await apiClient.get<IndustryVersion[]>(
    `${BASE_URL}/${industryId}/versions?${params.toString()}`
  );
  return response.data;
};

/**
 * Get current active version
 */
export const getCurrentVersion = async (
  industryId: string
): Promise<IndustryVersion> => {
  const response = await apiClient.get<IndustryVersion>(
    `${BASE_URL}/${industryId}/versions/current`
  );
  return response.data;
};

/**
 * Get specific version by ID
 */
export const getVersion = async (
  industryId: string,
  versionId: string
): Promise<IndustryVersion> => {
  const response = await apiClient.get<IndustryVersion>(
    `${BASE_URL}/${industryId}/versions/${versionId}`
  );
  return response.data;
};

// ============================================================================
// ANALYTICS
// ============================================================================

export interface IndustryTimeline {
  industryId: string;
  totalVersions: number;
  timeline: Array<{
    versionId: string;
    semanticVersion: string;
    reportPeriod: string;
    createdAt: string;
    changeType: string;
    materialityScore: number;
    materialityAssessment: string;
    changeDescription: string;
  }>;
}

/**
 * Get version timeline for an industry
 */
export const getIndustryTimeline = async (
  industryId: string
): Promise<IndustryTimeline> => {
  const response = await apiClient.get<IndustryTimeline>(
    `${BASE_URL}/${industryId}/timeline`
  );
  return response.data;
};

export interface IndustryUsage {
  industryId: string;
  activeCaseCount: number;
  totalCaseReferences: number;
  lastCaseReferenceAt?: string;
  dataRefreshStatus: DataRefreshStatus;
}

/**
 * Get industry usage statistics
 */
export const getIndustryUsage = async (
  industryId: string
): Promise<IndustryUsage> => {
  const response = await apiClient.get<IndustryUsage>(
    `${BASE_URL}/${industryId}/usage`
  );
  return response.data;
};

// ============================================================================
// COMPETITIVE LANDSCAPE ENRICHMENT
// ============================================================================

export interface EnrichCompetitiveLandscapeResponse {
  message: string;
  enrichedCount: number;
  marketLeaders: MarketLeader[];
  companiesInResearch: number;
}

export interface CheckCompanyExistsResponse {
  exists: boolean;
  companyId?: string;
  cik?: string;
  ticker?: string;
  companyName?: string;
  matchConfidence?: number;
  message?: string;
}

export interface AddCompanyToResearchResponse {
  companyId: string;
  message: string;
  cik?: string | null;
  ticker?: string | null;
  alreadyExists: boolean;
}

/**
 * Enrich competitive landscape with CIK numbers and Company Research status
 */
export const enrichCompetitiveLandscape = async (
  industryId: string
): Promise<EnrichCompetitiveLandscapeResponse> => {
  const response = await apiClient.post<EnrichCompetitiveLandscapeResponse>(
    `${BASE_URL}/${industryId}/competitive-landscape/enrich`
  );
  return response.data;
};

/**
 * Check if a company exists in Company Research
 */
export const checkCompanyExists = async (
  companyName: string,
  cik?: string
): Promise<CheckCompanyExistsResponse> => {
  const params = new URLSearchParams();
  params.append('company_name', companyName);
  if (cik) params.append('cik', cik);

  const response = await apiClient.get<CheckCompanyExistsResponse>(
    `${BASE_URL}/companies/check-exists?${params.toString()}`
  );
  return response.data;
};

/**
 * Add a company from competitive landscape to Company Research
 *
 * When `industryId` is supplied and `country` is not, the backend infers the
 * country from the industry's geographic scope — so a European industry will
 * route all added companies through the UK Companies House pipeline.
 */
export const addCompanyToResearch = async (
  companyName: string,
  cik: string | null | undefined,
  ticker?: string,
  createdBy: string = 'user_001',
  country?: string,
  industryId?: string,
  timeRange?: string,
): Promise<AddCompanyToResearchResponse> => {
  const params = new URLSearchParams();
  params.append('company_name', companyName);
  if (cik) params.append('cik', cik);
  if (ticker) params.append('ticker', ticker);
  params.append('created_by', createdBy);
  if (country) params.append('country', country);
  if (industryId) params.append('industry_id', industryId);
  if (timeRange) params.append('time_range', timeRange);

  const response = await apiClient.post<AddCompanyToResearchResponse>(
    `${BASE_URL}/companies/add-from-competitive-landscape?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// ADD COMPETITOR TO COMPETITIVE LANDSCAPE
// ============================================================================

export interface AddCompetitorResponse {
  message: string;
  company: MarketLeader;
  totalCompetitors: number;
  alreadyExists: boolean;
}

/**
 * Add a custom competitor to the competitive landscape with AI-generated analysis
 */
export const addCompetitorToLandscape = async (
  industryId: string,
  companyName: string,
  cik?: string,
  ticker?: string,
  country: string = 'US'
): Promise<AddCompetitorResponse> => {
  const params = new URLSearchParams();
  params.append('company_name', companyName);
  if (cik) params.append('cik', cik);
  if (ticker) params.append('ticker', ticker);
  params.append('country', country);

  const response = await apiClient.post<AddCompetitorResponse>(
    `${BASE_URL}/${industryId}/competitive-landscape/add-competitor?${params.toString()}`
  );
  return response.data;
};

// ============================================================================
// INDUSTRY FINANCIAL BENCHMARKS
// ============================================================================

/**
 * Fetch aggregated financial benchmarks for an industry.
 * Backend aggregates market leaders from competitive landscape + caches 24h.
 */
export const getIndustryBenchmarks = async (
  industryId: string,
  years: number = 10,
  country?: string | null
): Promise<IndustryBenchmarkResponse> => {
  const params = new URLSearchParams();
  params.append('years', years.toString());
  // Case company's country. The backend uses it for market leaders that carry
  // no country of their own; without it they default to US, which sent Indian
  // peers (and the Indian case company itself) to the wrong data provider.
  if (country) params.append('country', country);

  const response = await apiClient.get<IndustryBenchmarkResponse>(
    `/v2/financial/industry/${industryId}?${params.toString()}`
  );
  return response.data;
};

const STALE_TIME_30_MIN = 1000 * 60 * 30;

/**
 * React Query hook for industry financial benchmarks.
 * Disabled when industryId is null/undefined.
 */
export const useIndustryBenchmarks = (
  industryId: string | null | undefined,
  years: number = 10,
  country?: string | null
) =>
  useQuery({
    queryKey: ['financial', 'industry-benchmark', industryId, years, country ?? null],
    queryFn: () => getIndustryBenchmarks(industryId!, years, country),
    enabled: !!industryId,
    staleTime: STALE_TIME_30_MIN,
    select: (data) => ({
      ...data,
      // Ensure aggregated_data is sorted ascending by year for charts
      aggregated_data: [...(data.aggregated_data ?? [])].sort((a, b) => a.year - b.year),
    }),
  });
